import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, PlusCircle } from "lucide-react";
import React, { useMemo, useState } from "react";

import { FlowTag } from "@/components/Tags/FlowTag";
import {
  WorkflowTag,
  TagsService,
  TagResponse,
  Tag,
  EntityType,
} from "@/generated";
import { useSpotUser } from "@/utils/spotUser";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { SpotToast } from "@/components/SpotToast";
import { ANALYTICS_EVENTS, analytics_track } from "@/utils/mixpanel";
import { FlowTagModal } from "./FlowTagModal";
import { TagActionsMenu } from "./TagActionsMenu";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../shadcn/dropdown-menu";
import { Skeleton } from "../shadcn/skeleton";
import { useDisclosure } from "@/utils/useDisclosure";

type EntityTag = WorkflowTag | TagResponse | Tag;

/* Menu on the entity row */
export const FlowTagsRowMenu = ({
  entityTags,
  entityId,
  entityType,
  isMenuOpen,
  setIsMenuOpen,
  onEditTag,
  onDeleteTag,
}: {
  entityTags: EntityTag[];
  entityId: string;
  entityType: EntityType;
  isMenuOpen: boolean;
  setIsMenuOpen: (value: boolean) => void;
  onEditTag: (tag: TagResponse) => void;
  onDeleteTag: (tag: TagResponse) => void;
}) => {
  const { spotGroup } = useSpotUser();
  const queryClient = useQueryClient();

  const { data: tagsData, isLoading: isTagsLoading } = useRemoteData(
    DATA_KEYS.TAGS,
    {
      path: { group_id: spotGroup.id },
    }
  );

  const groupTags = useMemo(
    () =>
      entityTags.map((t) => {
        if ("id" in t) return t.id; // TagResponse and Tag
        return t.value; // WorkflowTag (legacy)
      }),
    [entityTags]
  );

  const tagModalControls = useDisclosure();
  const [actionsTagId, setActionsTagId] = useState<string>();

  /* Tag operations */
  const tagEntityMutation = useMutation({
    mutationFn: TagsService.tagEntity,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.TAGS],
      });
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.WORKFLOWS],
      });

      if (entityType === "assistant") {
        queryClient.invalidateQueries({
          queryKey: [DATA_KEYS.ASSISTANTS],
        });
      }
      if (entityType === "knowledge_base") {
        queryClient.invalidateQueries({
          queryKey: [DATA_KEYS.KNOWLEDGE_BASES_V2],
        });
      }
      if (entityType === "table") {
        queryClient.invalidateQueries({
          queryKey: [DATA_KEYS.TABLES],
        });
      }
    },
    onError: (error) => {
      // Revert instant state on error
      SpotToast.error("Error adding tag. Please try again later.");
    },
  });

  const untagEntityMutation = useMutation({
    mutationFn: TagsService.untagEntity,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.TAGS],
      });
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.WORKFLOWS],
      });

      if (entityType === "assistant") {
        queryClient.invalidateQueries({
          queryKey: [DATA_KEYS.ASSISTANTS],
        });
      }
      if (entityType === "knowledge_base") {
        queryClient.invalidateQueries({
          queryKey: [DATA_KEYS.KNOWLEDGE_BASES_V2],
        });
      }
      if (entityType === "table") {
        queryClient.invalidateQueries({
          queryKey: [DATA_KEYS.TABLES],
        });
      }
    },
    onError: (error) => {
      SpotToast.error("Error removing tag. Please try again later.");
    },
  });

  const createTagMutation = useMutation({
    mutationFn: TagsService.createTag,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.TAGS],
      });
      // Add the new tag to instant state immediately
      const newTag = data.data;

      // After creating tag, immediately tag the entity
      tagEntityMutation.mutate({
        path: {
          group_id: spotGroup.id,
          tag_id: data.data.id,
        },
        body: {
          entity_id: entityId,
          entity_type: entityType,
        },
      });
    },
    onError: (error) => {
      // Revert instant state if tag creation fails
      if ((error as any)?.response?.status === 409) {
        SpotToast.error("Tag already exists");
      } else {
        SpotToast.error("Error creating tag. Please try again later.");
      }
    },
  });

  const handleTagCreate = (tag: { name: string; color: string }) => {
    analytics_track(ANALYTICS_EVENTS.FLOWS_TAGS_CREATE);
    createTagMutation.mutate({
      path: { group_id: spotGroup.id },
      body: {
        name: tag.name,
        color: tag.color,
      },
    });
  };

  const handleTagChange = (tagId: string, checked: boolean) => {
    analytics_track(ANALYTICS_EVENTS.FLOWS_TAGS_UPDATE);

    if (checked) {
      // Add tag to entity
      const tag = tagsData?.find((t) => t.id === tagId);
      if (tag) {
        tagEntityMutation.mutate({
          path: {
            group_id: spotGroup.id,
            tag_id: tagId,
          },
          body: {
            entity_id: entityId,
            entity_type: entityType,
          },
        });
      }
    } else {
      // Remove tag from entity
      untagEntityMutation.mutate({
        path: {
          group_id: spotGroup.id,
          tag_id: tagId,
          entity_id: entityId,
        },
        query: {
          entity_type: entityType,
        },
      });
    }
  };

  return (
    <>
      <DropdownMenu
        open={isMenuOpen}
        onOpenChange={setIsMenuOpen}
        modal={false}
      >
        <DropdownMenuTrigger className="w-fit" asChild>
          <div
            data-testid="row-add-tag"
            className={`[tr:hover_&]:opacity-100 opacity-0 transition-opacity duration-200 ${isMenuOpen ? "opacity-100" : "opacity-0"}`}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <PlusCircle
              className={`cursor-pointer hover:text-foreground transition-colors duration-200 ${isMenuOpen ? "text-foreground " : "text-muted-foreground"}`}
              size="1.25rem"
              strokeWidth="1.5px"
            />
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side="right"
          align="start"
          className="flex max-h-[min(20rem,var(--radix-dropdown-menu-content-available-height))] flex-col overflow-hidden"
          onContextMenu={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          {/* With no tags yet, only New tag shows: no empty list above it. */}
          {(isTagsLoading || !!tagsData?.length) && (
            <>
              <div className="min-h-0 flex-1 overflow-y-auto">
                {isTagsLoading ? (
                  <>
                    <DropdownMenuItem>
                      <Skeleton className="w-full h-6 rounded-md" />
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <Skeleton className="w-full h-6 rounded-md" />
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <Skeleton className="w-full h-6 rounded-md" />
                    </DropdownMenuItem>
                  </>
                ) : (
                  tagsData?.map((tag, index) => (
                    <div key={`${tag.id}-${index}`} className="relative">
                      <DropdownMenuCheckboxItem
                        checked={groupTags?.includes(tag.id)}
                        onCheckedChange={(checked) =>
                          handleTagChange(tag.id, !!checked)
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        onContextMenu={() => setActionsTagId(tag.id)}
                      >
                        <FlowTag tag={tag} />
                      </DropdownMenuCheckboxItem>
                      <TagActionsMenu
                        open={actionsTagId === tag.id}
                        onOpenChange={(open) =>
                          setActionsTagId(open ? tag.id : undefined)
                        }
                        onEdit={() => {
                          setIsMenuOpen(false);
                          onEditTag(tag);
                        }}
                        onDelete={() => {
                          setIsMenuOpen(false);
                          onDeleteTag(tag);
                        }}
                      >
                        <span
                          aria-hidden
                          className="pointer-events-none absolute inset-y-0 right-0"
                        />
                      </TagActionsMenu>
                    </div>
                  ))
                )}
              </div>
              <DropdownMenuSeparator className="shrink-0" />
            </>
          )}
          <DropdownMenuItem
            className="shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              tagModalControls.onOpen();
              setIsMenuOpen(false);
            }}
          >
            <Plus size="1.25rem" strokeWidth="1.5px" />
            New tag
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {tagModalControls.isOpen && (
        <FlowTagModal
          isOpen
          onOpenChange={tagModalControls.onClose}
          onConfirm={handleTagCreate}
        />
      )}
    </>
  );
};
